<script setup lang="ts">
import { computed } from 'vue'

/**
 * CoreCard
 * Composable card surface that stays layout-agnostic.
 *
 * Slots:
 * - default: Main card content.
 * - media: Optional media region (image, video, etc).
 * - header: Optional header block.
 * - actions: Optional actions row.
 * - footer: Optional footer block.
 * - loader: Optional loading indicator rendered when `loading` is true.
 *
 * Usage: Prefer composing layout with slots + utilities (e.g., CoreCardItem).
 */
type CoreCardVariant = 'surface' | 'elevated' | 'outlined' | 'tonal' | 'flat'
type CoreCardDensity = 'compact' | 'comfortable' | 'default'
type CoreCardRounded = 'none' | 'sm' | 'md' | 'lg' | 'pill'
type CoreCardAs = 'div' | 'article' | 'section' | 'a' | 'button'
type CoreCardElevation = 0 | 1 | 2 | 3 | 4

const props = withDefaults(
  defineProps<{
    /**
     * Root element tag. Use `button` or `a` for interactive cards.
     */
    as?: CoreCardAs
    /**
     * Visual style for the surface. Does not imply layout.
     */
    variant?: CoreCardVariant
    /**
     * Padding density for the card surface.
     */
    density?: CoreCardDensity
    /**
     * Corner radius token.
     */
    rounded?: CoreCardRounded
    /**
     * Elevation level (maps to design tokens).
     */
    elevation?: CoreCardElevation
    /**
     * Adds a subtle border to the card surface.
     */
    border?: boolean
    /**
     * Enables hover affordance (shadow).
     */
    hover?: boolean
    /**
     * Disables interaction and dims the surface.
     */
    disabled?: boolean
    /**
     * Shows loader slot and applies busy state.
     */
    loading?: boolean
  }>(),
  {
    as: 'div',
    variant: 'surface',
    density: 'default',
    rounded: 'md',
    elevation: 0,
    border: false,
    hover: false,
    disabled: false,
    loading: false,
  }
)

const isInteractive = computed(() => props.as === 'button' || props.as === 'a')

const variantClasses: Record<CoreCardVariant, string> = {
  surface: 'bg-surface-raised text-on-surface',
  elevated: 'bg-surface-solid text-on-surface',
  outlined: 'border border-border bg-surface-raised text-on-surface',
  tonal: 'bg-surface-interactive text-on-surface',
  flat: 'bg-transparent text-on-surface',
}

const densityClasses: Record<CoreCardDensity, string> = {
  compact: 'p-3',
  comfortable: 'p-4',
  default: 'p-6',
}

const roundedClasses: Record<CoreCardRounded, string> = {
  none: 'rounded-none',
  sm: 'rounded-ui',
  md: 'rounded-card',
  lg: 'rounded-xl',
  pill: 'rounded-full',
}

const elevationClasses: Record<CoreCardElevation, string> = {
  0: 'shadow-none',
  1: 'shadow-floating-low',
  2: 'shadow-floating',
  3: 'shadow-floating-variant',
  4: 'shadow-floating',
}

const rootClasses = computed(() => {
  const classes = [
    'core-card',
    'relative',
    variantClasses[props.variant],
    densityClasses[props.density],
    roundedClasses[props.rounded],
    elevationClasses[props.elevation],
    props.border ? 'border border-border' : '',
  ]

  if (isInteractive.value) {
    classes.push('focus-visible:outline-none', 'focus-visible:ring-2', 'focus-visible:ring-[var(--theme-focus-ring)]', 'focus-visible:ring-offset-1', 'focus-visible:ring-offset-[var(--theme-focus-ring-offset)]')
    classes.push(props.disabled || props.loading ? 'cursor-not-allowed' : 'cursor-pointer')
  }

  if (props.hover && !props.disabled && !props.loading) {
    classes.push('transition-default', 'duration-default', 'hover:border-border-hover', 'hover:bg-surface-overlay', 'hover:shadow-interactive')
  }

  if (props.disabled || props.loading) {
    classes.push('opacity-60', 'pointer-events-none')
  }

  return classes
})
</script>

<template>
  <component
    :is="as"
    :class="rootClasses"
    :disabled="as === 'button' ? disabled || loading : undefined"
    :aria-disabled="as !== 'button' && (disabled || loading) ? 'true' : undefined"
    :aria-busy="loading ? 'true' : undefined"
  >
    <slot name="loader" v-if="loading" />
    <slot name="media" />
    <slot name="header" />
    <slot />
    <slot name="actions" />
    <slot name="footer" />
  </component>
</template>
